import React, { useState, useMemo } from 'react';
import {
    BlockDefinition,
    BLOCK_DEFINITIONS,
} from '../../types/homepage';

const SIDEBAR_GROUPS: { label: string; types: string[] }[] = [
    { label: 'HEADER', types: ['header'] },
    { label: 'BANNERS', types: ['welcome-banner', 'hero-carousel'] },
    { label: 'CATEGORIES', types: ['categories-list', 'highlighted-categories'] },
    { label: 'CHANNELS', types: ['all-channels', 'featured-channel'] },
    {
        label: 'CONTENT ROWS',
        types: ['medium-carousel', 'search-filter', 'quick-links', 'text-over-video', 'thumbnail-grid', 'vertical-list', 'horizontal-scroll', 'custom-promo'],
    },
    { label: 'FOOTER', types: ['footer'] },
];

interface BlockPickerModalProps {
    onSelect: (def: BlockDefinition) => void;
    onClose: () => void;
}

export default function BlockPickerModal({ onSelect, onClose }: BlockPickerModalProps) {
    const [selectedType, setSelectedType] = useState<string>(BLOCK_DEFINITIONS[0]?.type || '');
    const [isAdding, setIsAdding] = useState(false);

    const selectedDef = useMemo(
        () => BLOCK_DEFINITIONS.find((d) => d.type === selectedType) || BLOCK_DEFINITIONS[0],
        [selectedType]
    );

    const handleAdd = async () => {
        if (!selectedDef) return;
        setIsAdding(true);
        await onSelect(selectedDef);
        setIsAdding(false);
    };

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
            onClick={onClose}
        >
            <div
                style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 640, height: '80vh', maxHeight: 640, display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', overflow: 'hidden' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>Add a block</h2>
                        <p style={{ margin: '2px 0 0', fontSize: 12, color: '#9ca3af' }}>{`Choose a template — you'll configure it on the page next`}</p>
                    </div>
                    <button onClick={onClose} style={{ padding: 6, background: 'none', border: 'none', cursor: 'pointer', borderRadius: 8, color: '#9ca3af' }}>
                        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

                    {/* Sidebar */}
                    <div style={{ width: 220, flexShrink: 0, borderRight: '1px solid #f3f4f6', overflowY: 'auto', padding: '12px 8px' }}>
                        {SIDEBAR_GROUPS.map((group) => {
                            const defs = group.types
                                .map((t) => BLOCK_DEFINITIONS.find((d) => d.type === t))
                                .filter(Boolean) as BlockDefinition[];
                            if (!defs.length) return null;
                            return (
                                <div key={group.label} style={{ marginBottom: 16 }}>
                                    <p style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: '#d1d5db', textTransform: 'uppercase', margin: '0 0 4px 8px' }}>{group.label}</p>
                                    {defs.map((def) => {
                                        const isActive = def.type === selectedType;
                                        return (
                                            <button key={def.type} onClick={() => setSelectedType(def.type)}
                                                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 8, border: 'none', background: isActive ? '#eff6ff' : 'transparent', color: isActive ? '#1d4ed8' : '#374151', fontSize: 13, fontWeight: isActive ? 600 : 400, cursor: 'pointer', textAlign: 'left' }}>
                                                <span style={{ fontSize: 16, flexShrink: 0 }}>{def.icon}</span>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{def.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>

                    {/* Detail + Add button */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '28px 28px 20px', overflow: 'auto' }}>
                        {selectedDef && (
                            <>
                                <div style={{ fontSize: 44, marginBottom: 14 }}>{selectedDef.icon}</div>
                                <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700, color: '#111827' }}>{selectedDef.label}</h3>
                                <p style={{ margin: '0 0 24px', fontSize: 13.5, color: '#6b7280', lineHeight: 1.6, maxWidth: 380 }}>{selectedDef.description}</p>

                                <div style={{ flex: 1 }} />

                                <button onClick={handleAdd} disabled={isAdding}
                                    style={{ alignSelf: 'flex-start', padding: '10px 22px', borderRadius: 8, border: 'none', background: isAdding ? '#93c5fd' : '#2563eb', color: 'white', fontWeight: 600, fontSize: 14, cursor: isAdding ? 'not-allowed' : 'pointer' }}>
                                    {isAdding ? 'Adding…' : `Add ${selectedDef.label}`}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}